// src/Profile.js - FIXED BILLING TAB
import React, { useState, useEffect, useCallback } from 'react';
import api, { API_BASE } from './apiClient';
import {
  Box,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Tabs,
  Tab,
  Avatar,
  CircularProgress,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import Cropper from 'react-easy-crop';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import { useSnackbar } from 'notistack';

// Country list
const countries = [
  { code: 'IN', name: 'India' },
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  // Add more as needed
];

function Profile({ open, onClose, userId, onLogout, isWorkspaceOwner = false }) {
  const [tab, setTab] = useState(0);
  const [data, setData] = useState({});
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [newPhotoFile, setNewPhotoFile] = useState(null); // Only set when new file selected
  const [photoPreview, setPhotoPreview] = useState(''); // URL from server or blob
  const [completion, setCompletion] = useState(0);
  const [workspaces, setWorkspaces] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [notificationPrefs, setNotificationPrefs] = useState({ email: true, sms: false });
  const [isLicensed, setIsLicensed] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState('');
  const [sessions, setSessions] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && userId) {
      loadProfile();
      if (isWorkspaceOwner) {
        loadWorkspaces();
        loadDocuments();
      }
    }
  }, [open, userId, isWorkspaceOwner]);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/profile', { params: { user_id: userId } });
      const profile = res.data || {};
      Object.keys(profile).forEach(key => {
        if (profile[key] === null) profile[key] = '';
      });
      setData(profile);
      setIsLicensed(profile.license_type === 'licensed');
      const baseUrl = api.defaults.baseURL && /^https?:/i.test(api.defaults.baseURL)
        ? api.defaults.baseURL
        : (API_BASE && /^https?:/i.test(API_BASE) ? API_BASE : window.location.origin);
      setPhotoPreview(profile.photo_url || (profile.photo ? `${baseUrl}/uploads/${profile.photo}` : ''));
      setCompletion(calculateCompletion(profile));
    } catch (err) {
      enqueueSnackbar('Error loading profile', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadWorkspaces = async () => {
    try {
      const res = await api.get('/api/workplaces', { params: { user_id: userId } });
      setWorkspaces(res.data || []);
    } catch (err) {
      enqueueSnackbar('Error loading workspaces', { variant: 'error' });
    }
  };

  const loadDocuments = async () => {
    try {
      const res = await api.get('/api/user-documents', { params: { user_id: userId } }).catch(() => ({ data: [] }));
      setDocuments(res.data || []);
    } catch (err) {
      setDocuments([]);
    }
  };

  const calculateCompletion = (profile) => {
    const fields = ['first_name','last_name','job_title','about','photo','address','billing_address','pan','phone','country'];
    const filled = fields.filter(f => profile[f]?.trim()).length;
    return Math.round((filled / fields.length) * 100);
  };
  const validatePAN = (pan) => /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(pan);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);
  

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const blobUrl = URL.createObjectURL(file);
      setNewPhotoFile(file);
      setPhotoPreview(blobUrl);
    }
  };

  const handlePhotoSave = async () => {
    if (!newPhotoFile) return enqueueSnackbar('No new photo to save', { variant: 'info' });
    try {
      const croppedImage = await getCroppedImg(photoPreview, croppedAreaPixels);
      const formData = new FormData();
      formData.append('user_id', userId);
      formData.append('photo', croppedImage, 'profile.jpg');
      const res = await api.put('/api/update-profile', formData);
      const updated = res.data || {};
      const baseUrl = api.defaults.baseURL || API_BASE || window.location.origin;
      const nextPhoto =
        updated.photo_url ||
        (updated.photo ? `${baseUrl}/uploads/${updated.photo}` : null) ||
        (newPhotoFile ? URL.createObjectURL(newPhotoFile) : null);
      if (nextPhoto) {
        setPhotoPreview(nextPhoto);
      }
      enqueueSnackbar('Photo updated!', { variant: 'success' });
      setNewPhotoFile(null);
      setData((prev) => ({ ...prev, photo: nextPhoto || prev.photo }));
      loadProfile();
    } catch (err) {
      enqueueSnackbar('Failed to save photo', { variant: 'error' });
    }
  };

  const getCroppedImg = (imageSrc, pixelCrop) => {
    return new Promise((resolve) => {
      const image = new Image();
      image.src = imageSrc;
      image.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height);
        canvas.toBlob((blob) => resolve(blob), 'image/jpeg');
      };
    });
  };

  const handleUpdate = async (fields) => {
    const formData = new FormData();
    formData.append('user_id', userId);
    Object.entries(fields).forEach(([k, v]) => formData.append(k, v ?? ''));
    try {
      await api.put('/api/update-profile', formData);
      enqueueSnackbar('Saved!', { variant: 'success' });
      loadProfile();
    } catch (err) {
      enqueueSnackbar('Save failed', { variant: 'error' });
    }
  };

  const handleUploadDocuments = async (e) => {
    const files = e.target.files;
    if (!files.length) return;
    const formData = new FormData();
    formData.append('user_id', userId);
    Array.from(files).forEach(f => formData.append('documents', f));
    try {
      await api.post('/api/upload-documents', formData);
      enqueueSnackbar('Documents uploaded', { variant: 'success' });
      loadDocuments();
    } catch (err) {
      enqueueSnackbar('Upload failed', { variant: 'error' });
    }
  };

  const handleCreateWorkspace = async (name) => {
    if (!name.trim()) return enqueueSnackbar('Enter workspace name', { variant: 'warning' });
    try {
      await api.post('/api/workplaces', { name: name.trim(), owner_id: userId });
      enqueueSnackbar('Workspace created', { variant: 'success' });
      loadWorkspaces();
    } catch (err) {
      enqueueSnackbar('Failed to create workspace', { variant: 'error' });
    }
  };

  const handleChangePassword = async () => {
  if (newPassword !== confirmNewPassword) return enqueueSnackbar('Passwords do not match', { variant: 'warning' });
  if (newPassword.length < 6) return enqueueSnackbar('Password must be 6+ characters', { variant: 'warning' });
  try {
    await api.put('/api/update-profile', { user_id: userId, old_password: oldPassword, password: newPassword });
    enqueueSnackbar('Password updated', { variant: 'success' });
    setOldPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
  } catch (err) {
    enqueueSnackbar('Failed to update password', { variant: 'error' });
  }
};

  const handleDeleteAccount = () => {
    if (!window.confirm('Are you sure you want to delete your account? This is permanent.')) return;
    try {
      api.delete(`/api/users/${userId}`, { params: { user_id: userId } });
      enqueueSnackbar('Account deleted', { variant: 'info' });
      onLogout();
    } catch (err) {
      enqueueSnackbar('Failed to delete account', { variant: 'error' });
    }
  };

  if (loading) return <LinearProgress />;

  const tabLabels = isWorkspaceOwner
    ? ['Personal', 'Billing & Compliance', 'Documents', 'Workspaces', 'Security']
    : ['Personal', 'Security'];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Profile <Tooltip title="Completion"><CircularProgress variant="determinate" value={completion} size={28} /> {completion}%</Tooltip>
      </DialogTitle>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} centered>
        {tabLabels.map((label) => (
          <Tab key={label} label={label} />
        ))}
      </Tabs>

      <DialogContent dividers>
        {/* PERSONAL TAB */}
        {tabLabels[tab] === 'Personal' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField label="First Name" value={data.first_name || ''} onChange={(e) => setData({ ...data, first_name: e.target.value })} />
            <TextField label="Last Name" value={data.last_name || ''} onChange={(e) => setData({ ...data, last_name: e.target.value })} />
            <TextField label="Job Title" value={data.job_title || ''} onChange={(e) => setData({ ...data, job_title: e.target.value })} />
            <TextField label="About Me" multiline rows={4} value={data.about || ''} onChange={(e) => setData({ ...data, about: e.target.value })} />

            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Avatar src={photoPreview} sx={{ width: 120, height: 120, mx: 'auto' }}>
                <AccountCircleIcon sx={{ fontSize: 80 }} />
              </Avatar>
            </Box>

            {newPhotoFile && (
              <Box sx={{ height: 320, mb: 3, position: 'relative' }}>
                <Cropper
                  image={photoPreview}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </Box>
            )}
            

            <Button variant="outlined" component="label" fullWidth>
              Choose Photo
              <input type="file" hidden accept="image/*" onChange={handlePhotoChange} />
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={handlePhotoSave}
              disabled={!newPhotoFile}
              fullWidth
              sx={{ mt: 1 }}
            >
              Save Photo
            </Button>

            <FormControl fullWidth>
              <InputLabel>Country</InputLabel>
              <Select
                label="Country"
                value={data.country || ''}
                onChange={(e) => setData({ ...data, country: e.target.value })}
              >
                {countries.map((c) => (
                  <MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField label="Phone" value={data.phone || ''} onChange={(e) => setData({ ...data, phone: e.target.value })} />

            <Button
              variant="contained"
              onClick={() =>
                handleUpdate({
                  first_name: data.first_name,
                  last_name: data.last_name,
                  job_title: data.job_title,
                  about: data.about,
                  country: data.country,
                  phone: data.phone,
                })
              }
            >
              Save Personal Info
            </Button>
          </Box>
        )}

        {isWorkspaceOwner && isLicensed && tabLabels[tab] === 'Billing & Compliance' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Typography variant="h6">Billing & Compliance</Typography>
            {Object.keys(data).length === 0 && <Typography color="text.secondary">No details yet.</Typography>}
            <TextField label="Address" multiline value={data.address || ''} onChange={(e) => setData({ ...data, address: e.target.value })} fullWidth />
            <TextField label="Billing Address" multiline value={data.billing_address || ''} onChange={(e) => setData({ ...data, billing_address: e.target.value })} fullWidth />
            <TextField label="PAN" value={data.pan || ''} onChange={(e) => setData({ ...data, pan: e.target.value })} error={data.pan && !validatePAN(data.pan)} helperText="Format: AAAAA9999A" fullWidth />
            <TextField label="GSTN" value={data.gstn || ''} onChange={(e) => setData({ ...data, gstn: e.target.value })} helperText="Optional; Format: 22AAAAA0000A1Z5" fullWidth />
            <Button onClick={() => handleUpdate({ address: data.address, billing_address: data.billing_address, pan: data.pan, gstn: data.gstn })}>Save Billing & Compliance</Button>
          </Box>
        )}

        {/* DOCUMENTS TAB */}
        {isWorkspaceOwner && tabLabels[tab] === 'Documents' && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>Verification Documents</Typography>
            <Typography>Storage Used: {documents.reduce((acc, d) => acc + (d.file_size || 0), 0) / (1024 * 1024)} MB</Typography>
            <Typography>Storage Used: {documents.reduce((acc, d) => acc + d.file_size, 0) / (1024 * 1024)} MB</Typography>
            <Button variant="contained" component="label" sx={{ mb: 2 }}>
              Upload Documents
              <input type="file" hidden multiple name="documents" onChange={handleUploadDocuments} />
            </Button>
            <List>
              {documents.length === 0 ? (
                <Typography color="text.secondary">No documents uploaded yet.</Typography>
              ) : (
                documents.map((doc) => (
                  <ListItem key={doc.id || doc.filename}>
                    <ListItemIcon>
                      {doc.original_name?.toLowerCase().endsWith('.pdf') ? <PictureAsPdfIcon /> : <ImageIcon />}
                    </ListItemIcon>
                    <ListItemText primary={doc.original_name || doc.filename} />
                  </ListItem>
                ))
              )}
            </List>
          </Box>
        )}

        {isWorkspaceOwner && tabLabels[tab] === 'Workspaces' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Typography variant="h6">Workspaces</Typography>
            <TextField label="New Workspace Name" onKeyDown={(e) => e.key === 'Enter' && handleCreateWorkspace(e.target.value)} fullWidth />
            <Typography variant="subtitle1">Your Workspaces:</Typography>
            {workspaces.length === 0 ? (
              <Typography color="text.secondary">No workspaces yet.</Typography>
            ) : (
              workspaces.map(ws => (
                <Typography key={ws.id}>{ws.name} (Role: {ws.role || 'Member'})</Typography>
              ))
            )}
          </Box>
        )}

        {tabLabels[tab] === 'Security' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Typography variant="h6">Security</Typography>
            <TextField label="Old Password" type="password" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} fullWidth />
            <TextField label="New Password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} fullWidth />
            <TextField label="Confirm New" type="password" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} fullWidth />
            <Button onClick={handleChangePassword}>Change Password</Button>
            <Typography variant="subtitle1">Active Sessions:</Typography>
            {sessions.length === 0 ? (
              <Typography color="text.secondary">No active sessions.</Typography>
            ) : (
              sessions.map(s => <Box>Device: {s.device} <Button size="small">Log Out</Button></Box>)
            )}
            <Typography variant="subtitle1">Login History:</Typography>
            {loginHistory.length === 0 ? (
              <Typography color="text.secondary">No login history.</Typography>
            ) : (
              loginHistory.map(h => <Box>{h.time} from {h.ip} ({h.device})</Box>)
            )}
            <FormControlLabel control={<Checkbox checked={twoFAEnabled} onChange={(e) => setTwoFAEnabled(e.target.checked)} />} label="Enable 2FA" />
            <Typography variant="subtitle1">Notifications:</Typography>
            <FormControlLabel control={<Checkbox checked={notificationPrefs.email} onChange={() => setNotificationPrefs({ ...notificationPrefs, email: !notificationPrefs.email })} />} label="Email" />
            <FormControlLabel control={<Checkbox checked={notificationPrefs.sms} onChange={() => setNotificationPrefs({ ...notificationPrefs, sms: !notificationPrefs.sms })} />} label="SMS" />
            <Button variant="outlined" onClick={async () => {
              const newOwner = prompt('Enter new owner user ID');
              if (newOwner) {
                await api.put(`/api/workplaces/${selectedWorkspace}/transfer`, { new_owner_id: newOwner, user_id: userId });
                enqueueSnackbar('Ownership transferred');
              }
            }}>Transfer Ownership</Button>
          </Box>
        )}

        {/* Other tabs (Billing, Workspaces, Security) unchanged */}
        {/* ... */}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default Profile;
